/**
 * Email HTML Formatter - ULTRA SIMPLIFIED for Spark Mail Compatibility
 *
 * This version removes ALL complex features that break modern email clients:
 * - NO VML (Vector Markup Language)
 * - NO MSO conditional comments
 * - NO <style> tags
 * - NO dark mode detection
 * - Maximum 3 table nesting levels
 * - Simple <a> tag buttons (no bulletproof VML)
 *
 * Trade-offs:
 * - Buttons may not look perfect in Outlook Desktop (but will still work)
 * - No dark mode prevention (emails may get color-inverted in dark mode)
 * - Border-radius removed for maximum compatibility
 *
 * Supported clients: Gmail, Apple Mail, Outlook.com, Yahoo, Spark Mail, ProtonMail
 */

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Escape URL
 */
function escapeUrl(url) {
  if (!url) return '';
  try {
    return encodeURI(url).replace(/'/g, '%27').replace(/"/g, '%22');
  } catch (e) {
    return '';
  }
}

/**
 * Build preheader text
 */
function buildPreheader({name, summary} = {}) {
  const safeName = escapeHtml(name || '');
  const safeSummary = escapeHtml(summary || 'Please review');
  return safeName ? `${safeName}, ${safeSummary}` : safeSummary;
}

/**
 * Create CTA link (download button styled as prominent link)
 */
function createButton(href, text) {
  const escapedHref = escapeUrl(href);
  const escapedText = escapeHtml(text);

  return `<a href="${escapedHref}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline; font-weight: 600;">${escapedText}</a>`;
}

/**
 * Create model section (with prominent blue borders)
 */
function createModelBox(content) {
  return `
<div style="margin: 0; padding: 8px 0; border-top: 2px solid #2563eb; border-bottom: 2px solid #2563eb;">
  ${content}
</div>`;
}

/**
 * Create discrepancy section (modern styling)
 */
function createDiscrepancyBox(title, content) {
  const titleHtml = title ? `<p style="margin: 0; padding: 0 0 8px 0; font-size: 14px; font-weight: 600; color: #1f2937;">${escapeHtml(title)}</p>` : '';
  return `
<div style="margin: 0; padding: 0 0 16px 0;">
  ${titleHtml}
  ${content}
</div>`;
}

/**
 * Create hold notice paragraph (tighter spacing for status messages)
 */
function createHoldNoticeParagraph(text) {
  return `<p style="margin: 0; padding: 0 0 6px 0;">${escapeHtml(text)}</p>`;
}

/**
 * Create hold notice section (simple text formatting)
 */
function createHoldNoticeBox(content) {
  return content;
}

/**
 * Main email template (modern base styling)
 * Minimized whitespace to prevent email clients from adding blank lines
 */
function getEmailTemplate(content, preheaderText = '') {
  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head><body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; font-size: 15px; line-height: 1.6; color: #374151;"><div style="padding: 0 20px 20px 20px;">${content}</div></body></html>`;
}

/**
 * Create paragraph (using padding for Sparkmail compatibility)
 */
function createParagraph(text) {
  return `<p style="margin: 0; padding: 0 0 10px 0;">${escapeHtml(text)}</p>`;
}

/**
 * Create section heading (modern styling)
 */
function createHeading(text) {
  return `<p style="margin: 0; padding: 0 0 8px 0; font-size: 15px; font-weight: 700; color: #1f2937;">${escapeHtml(text)}</p>`;
}

/**
 * Create regular link (standard hyperlink styling)
 */
function createLink(href, text, label = null) {
  const escapedHref = escapeUrl(href);
  const escapedText = escapeHtml(text);
  const ariaLabel = label ? ` aria-label="${escapeHtml(label)}"` : '';
  return `<a href="${escapedHref}" target="_blank" rel="noopener noreferrer"${ariaLabel} style="color: #2563eb; text-decoration: underline;">${escapedText}</a>`;
}

// Note: createSeparator and createThickDivider are currently unused but kept for potential future use
// /**
//  * Create horizontal rule separator (modern, subtle)
//  */
// function createSeparator() {
//   return `<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 24px 0;">`;
// }
//
// /**
//  * Create thick section divider (prominent)
//  */
// function createThickDivider() {
//   return `<hr style="border: none; border-top: 2px solid #2563eb; margin: 24px 0;">`;
// }

/**
 * Create signature (basic spacing)
 */
function createSignature(signatureContent) {
  return `
<div style="margin: 16px 0 0 0;">
  ${signatureContent}
</div>`;
}

/**
 * Create client portal notice (appears before signature in all emails)
 */
function createClientPortalNotice() {
  return `
<div style="margin: 20px 0 16px 0; padding: 12px 16px; border-top: 1px solid #0891b2; border-bottom: 1px solid #0891b2; background-color: #f0fdfa;">
  <p style="margin: 0 0 4px 0; font-size: 13px; font-weight: 600; color: #0e7490;">Client Portal Now Available</p>
  <p style="margin: 0; font-size: 12px; color: #155e75; line-height: 1.5;">Access your projects, files, and updates at <a href="https://portal.ab-civil.com" style="color: #0891b2; text-decoration: underline; font-weight: 500;">portal.ab-civil.com</a>. Log in with your email—most users already have access. Anyone can manage their team's access from within the portal.</p>
</div>`;
}

/**
 * Get label from filename
 */
function labelFromFilename(fn) {
  if (!fn) return 'File';
  const f = fn.toLowerCase();

  if (f.endsWith('.pdf')) return 'PDF document';
  if (f.endsWith('.json')) return 'Workflow JSON';
  if (f.includes('takeoff')) return 'Takeoff log';
  if (/\.(txt|log)$/.test(f)) return 'Log notes';
  if (/\.(png|jpg|jpeg|gif)$/.test(f)) return 'Image';
  if (f.endsWith('.vcl')) return 'Trimble VCL model';
  if (f.endsWith('.ttm')) return 'Trimble surface (TTM)';
  if (f.endsWith('.dxf')) return 'CAD drawing (DXF)';
  if (f.endsWith('.dwg')) return 'CAD drawing (DWG)';

  return 'File';
}

/**
 * Main conversion function
 */
export function formatEmailToHTML(
  plainText,
  userSignature = null,
  includeAttachmentText = true,
  attachmentData = {},
  preheaderData = {}
) {
  if (!plainText) {
    return getEmailTemplate(
      `<p style="margin: 0 0 16px 0;"><em>Email preview will appear here...</em></p>`,
      buildPreheader(preheaderData)
    );
  }

  const lines = plainText.split('\n');
  let htmlParts = [];

  // Track sections
  let inModelSection = false;
  let inDiscrepancySection = false;
  let currentDiscrepancy = null;
  let modelSectionLines = [];
  let discrepancies = [];
  let holdNoticeLines = [];
  let inHoldNotice = false;

  // Discrepancy type detection - match any line ending with specific keywords + colon
  // This matches all database types: "PDF/CAD File Discrepancy:", "Utility Discrepancy:", etc.
  const isDiscrepancyTitle = (line) => {
    const titlePatterns = [
      /Discrepancy:$/,
      /Clarification:$/,
      /Overlays:$/,
      /Missing.*:$/,
      /Profile.*:$/,
      /Grading.*:$/,
      /Utility.*:$/,
      /Question:$/,
      /RFI:$/,
      /Hold:$/
    ];
    return titlePatterns.some(pattern => pattern.test(line.trim()));
  };

  // Process each line
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Detect hold notice
    if (trimmed.includes('This project is being placed on hold')) {
      inHoldNotice = true;
      holdNoticeLines = [];
      continue;
    }

    if (inHoldNotice) {
      if (trimmed === '' && holdNoticeLines.length > 0) {
        inHoldNotice = false;
        // Don't add hold notice here - save for end
      } else if (trimmed) {
        holdNoticeLines.push(trimmed);
      }
      continue;
    }

    // Detect model section
    if (trimmed.includes('3D Model Files Download') || trimmed.includes('Model Export Files')) {
      inModelSection = true;
      inDiscrepancySection = false;
      modelSectionLines = [trimmed];
      continue;
    }

    // Detect discrepancy section header
    if (trimmed === 'Related Discrepancies:') {
      inModelSection = false;
      inDiscrepancySection = true;
      continue;
    }

    // Detect individual discrepancy (both standalone and within section)
    // Check if this line is a discrepancy title using our pattern matcher
    if (isDiscrepancyTitle(trimmed)) {
      inModelSection = false;
      inDiscrepancySection = true; // Enter discrepancy mode
      if (currentDiscrepancy) {
        discrepancies.push(currentDiscrepancy);
      }
      currentDiscrepancy = { title: trimmed.replace(/:$/, ''), lines: [] }; // Remove trailing colon
      continue;
    }

    // Also support old pattern: any line ending with ':' after "Related Discrepancies:"
    if (inDiscrepancySection && trimmed.endsWith(':') && trimmed.length > 1 && !currentDiscrepancy) {
      currentDiscrepancy = { title: trimmed.replace(/:$/, ''), lines: [] };
      continue;
    }

    // Detect end of discrepancy section - certain phrases indicate we're back to regular content
    if (inDiscrepancySection && currentDiscrepancy && trimmed) {
      if (trimmed.startsWith('Additionally,') || trimmed.startsWith('Please let me know') || trimmed.startsWith('Thank you')) {
        // Save current discrepancy and exit discrepancy mode
        discrepancies.push(currentDiscrepancy);
        currentDiscrepancy = null;
        inDiscrepancySection = false;
        // This line is regular content, not part of discrepancy
        htmlParts.push(createParagraph(trimmed));
        continue;
      }
    }

    // Add to current section
    if (inModelSection && trimmed) {
      modelSectionLines.push(trimmed);
    } else if (currentDiscrepancy && trimmed) {
      currentDiscrepancy.lines.push(trimmed);
    } else if (!inModelSection && !inDiscrepancySection && trimmed) {
      // Regular line
      htmlParts.push(createParagraph(trimmed));
    }
  }

  // Add last discrepancy
  if (currentDiscrepancy) {
    discrepancies.push(currentDiscrepancy);
  }

  // Build model section
  if (modelSectionLines.length > 0) {
    let modelContent = '';
    let downloadLink = '';

    for (const line of modelSectionLines) {
      if (line.includes('3D Model Files Download Link:') || line.includes('Download Link:')) {
        const urlMatch = line.match(/(https?:\/\/[^\s]+)/);
        if (urlMatch) {
          downloadLink = urlMatch[1];
          modelContent += createHeading('3D Model Files Download:');
          modelContent += createButton(downloadLink, 'Download All Model Files');
        } else {
          modelContent += createParagraph(line);
        }
      } else if (line.includes('Included Formats:')) {
        modelContent += `<p style="margin: 0; padding: 0 0 8px 0; font-size: 13px; color: #6b7280;"><em>${escapeHtml(line)}</em></p>`;
      } else {
        modelContent += createParagraph(line);
      }
    }

    htmlParts.push(createModelBox(modelContent));

    // Spacing after model section
    htmlParts.push(`<div style="padding: 20px 0;"></div>`);
  }

  // Build discrepancy sections (no border wrapper)
  if (discrepancies.length > 0) {
    for (const disc of discrepancies) {
      let discContent = '';

      for (const line of disc.lines) {
        if (line.startsWith('File link:')) {
          const urlMatch = line.match(/File link:\s*(https?:\/\/[^\s]+)/);
          if (urlMatch) {
            const url = urlMatch[1];
            const filename = url.split('/').pop().split('?')[0];
            const label = labelFromFilename(filename);
            discContent += `<p style="margin: 0; padding: 0 0 8px 0;"><span style="font-size: 13px; color: #6b7280; font-weight: 500;">File link:</span> ${createLink(url, label)}</p>`;
          } else {
            discContent += createParagraph(line);
          }
        } else if (line.startsWith('Video Explanation:')) {
          const urlMatch = line.match(/Video Explanation:\s*(https?:\/\/[^\s]+)/);
          if (urlMatch) {
            discContent += `<p style="margin: 0; padding: 0 0 8px 0;"><span style="font-size: 13px; color: #6b7280; font-weight: 500;">Video Explanation:</span> ${createLink(urlMatch[1], 'Video Link', 'Video explanation')}</p>`;
          } else {
            discContent += createParagraph(line);
          }
        } else {
          discContent += createParagraph(line);
        }
      }

      htmlParts.push(createDiscrepancyBox(disc.title, discContent));
    }
  }

  // Add hold notice at the end if present
  if (holdNoticeLines.length > 0) {
    const holdContent = holdNoticeLines.map(line => createHoldNoticeParagraph(line)).join('');
    htmlParts.push(createHoldNoticeBox(holdContent));
    // Add spacing after hold notice section
    htmlParts.push(`<div style="padding: 8px 0;"></div>`);
  }

  // Client portal notice (before signature)
  htmlParts.push(createClientPortalNotice());

  // Spacing before signature
  if (userSignature) {
    htmlParts.push(`<div style="padding: 20px 0;"></div>`);
    htmlParts.push(createSignature(userSignature));
  }

  // Combine all parts (no newlines to prevent email client spacing issues)
  const bodyContent = htmlParts.join('');

  return getEmailTemplate(bodyContent, buildPreheader(preheaderData));
}

/**
 * Build structured email directly from data (Option A: Hybrid Approach)
 * No text parsing - generates HTML directly from structured data
 *
 * @param {Object} data - Structured email data
 * @param {string} data.greeting - Email greeting (e.g., "Hi John,")
 * @param {string} data.downloadPlaceholder - Placeholder text for download link
 * @param {string} data.downloadUrl - Actual download URL (or placeholder)
 * @param {Array<string>} data.includedFormats - Array of format names
 * @param {Array<Object>} data.discrepancies - Array of discrepancy objects
 * @param {string} data.discrepancies[].type - Discrepancy type
 * @param {string} data.discrepancies[].videoUrl - Video explanation URL (optional)
 * @param {Array<Object>} data.discrepancies[].fileLinks - Array of file objects (optional)
 * @param {string} data.discrepancies[].fileLinks[].url - File download URL
 * @param {string} data.discrepancies[].fileLinks[].filename - File name to display as link text
 * @param {string} data.customMessage - User's custom message (optional)
 * @param {Array<string>} data.footerMessages - Array of footer messages to display
 * @param {string} data.signature - User's email signature HTML
 * @returns {string} Complete HTML email
 */
export function buildStructuredEmail({
  greeting = "Hi,",
  downloadPlaceholder = "[Link will be generated when sending]",
  downloadUrl = null,
  includedFormats = [],
  templateMessages = [],
  discrepancies = [],
  holdNoticeMessages = [],
  customMessage = "",
  footerMessages = [],
  signature = ""
}) {
  let htmlParts = [];

  // Greeting (tighter spacing)
  htmlParts.push(`<p style="margin: 0; padding: 0 0 8px 0;">${escapeHtml(greeting)}</p>`);

  // Hold notice messages FIRST (status update)
  // Filter out empty/whitespace-only messages
  const cleanHoldMessages = holdNoticeMessages.filter(msg => msg && msg.trim());
  if (cleanHoldMessages.length > 0) {
    let holdContent = '';
    cleanHoldMessages.forEach(msg => {
      holdContent += createHoldNoticeParagraph(msg.trim());
    });
    htmlParts.push(createHoldNoticeBox(holdContent));
    // Add spacing after hold notice section
    htmlParts.push(`<div style="padding: 8px 0;"></div>`);
  }

  // Model download section - only include for export emails (when includedFormats is provided)
  // Discrepancy emails won't have formats and should skip this section entirely
  const hasDownloadSection = includedFormats.length > 0;

  if (hasDownloadSection) {
    htmlParts.push(createParagraph("Please see the link below for your model files:"));

    const actualDownloadUrl = downloadUrl || downloadPlaceholder;

    // Model download box
    let modelContent = createHeading("3D Model Files Download:");
    modelContent += createButton(actualDownloadUrl, "Download All Model Files");

    if (includedFormats.length > 0) {
      modelContent += `<p style="margin: 0; padding: 0 0 8px 0; font-size: 13px; color: #6b7280;"><em>Included Formats: ${includedFormats.map(f => escapeHtml(f)).join(", ")}</em></p>`;
    }

    htmlParts.push(createModelBox(modelContent));

    // Spacing after model section
    htmlParts.push(`<div style="padding: 20px 0;"></div>`);
  }

  // Template messages (intro for discrepancies - "Please see attached files...")
  // Filter out empty/whitespace-only messages
  templateMessages.filter(msg => msg && msg.trim()).forEach(msg => {
    htmlParts.push(createParagraph(msg.trim()));
  });

  // Discrepancies section - attachments and links
  if (discrepancies.length > 0) {
    discrepancies.forEach((disc, index) => {
      const hasVideo = disc.videoUrl && disc.videoUrl.trim();
      const hasFiles = disc.fileLinks && Array.isArray(disc.fileLinks) && disc.fileLinks.length > 0;

      // Add discrepancy type heading if provided
      if (disc.type) {
        htmlParts.push(`<p style="margin: ${index > 0 ? '16px' : '0'} 0 8px 0; font-size: 14px; font-weight: 600; color: #1f2937;">${escapeHtml(disc.type)}:</p>`);
      }

      // Add video explanation if present
      if (hasVideo) {
        htmlParts.push(`<p style="margin: 0; padding: 0 0 12px 0;">Video: ${createLink(disc.videoUrl, 'Video Link', 'Video explanation')}</p>`);
      }

      // Add file links if present (multiple files supported)
      if (hasFiles) {
        disc.fileLinks.forEach(file => {
          if (file.url && file.filename) {
            htmlParts.push(`<p style="margin: 0; padding: 0 0 8px 0;">${createLink(file.url, file.filename)}</p>`);
          }
        });
      }
    });
  }

  // Footer messages (for backward compatibility with export emails)
  footerMessages.forEach(msg => {
    htmlParts.push(createParagraph(msg));
  });

  // Custom message (user's additional notes)
  if (customMessage && customMessage.trim()) {
    // Split by newlines and create paragraphs
    customMessage.trim().split('\n').forEach(line => {
      if (line.trim()) {
        htmlParts.push(createParagraph(line.trim()));
      }
    });
  }

  // Default closing if no custom message provided
  if (!footerMessages.length && !customMessage.trim()) {
    htmlParts.push(createParagraph("Please let me know if you have any questions."));
  }

  // "Thank you," line - always included as part of closing
  htmlParts.push(createParagraph("Thank you,"));

  // Client portal notice (before signature)
  htmlParts.push(createClientPortalNotice());

  // Signature (no separator)
  if (signature) {
    htmlParts.push(createSignature(signature));
  }

  // Combine all parts (no newlines to prevent email client spacing issues)
  const bodyContent = htmlParts.join('');

  return getEmailTemplate(bodyContent, buildPreheader());
}
